/**
 * 编辑焦点管理器（EditingFocusManager）
 *
 * 职责：管理装配体编辑焦点下的文档标题文本，并作为“当前正在编辑的 GLB 文件名称”的唯一真相来源。
 * - 进入“编辑零部件”后返回 “当前编辑零件名称 ← 装配体原名称”
 * - 退出后恢复为装配体原名称
 * - 纯数据状态管理，不依赖 Three.js、Vue 或任何渲染对象
 * - 通过 Engine/Viewer 统一出口暴露，web-client 仅调用读取
 *
 * 符合 SolidWorks “Editing Focus” 视图状态规范（不进入 Model Undo/Redo 栈）
 */

export class EditingFocusManager {
  /** 当前正在编辑的装配体（GLB 文件）名称（不含扩展名），作为标题和焦点管理的唯一来源 */
  private assemblyName: string | null = null;

  /** 当前正在编辑的零部件名称（零件或子装配体） */
  private editingComponentName: string | null = null;

  /**
   * 设置当前正在编辑的装配体名称。
   * 该方法是“正在编辑的 GLB 文件名称”的唯一持久化入口。
   * 调用后会清空之前的编辑零部件焦点，避免跨文档状态污染。
   *
   * @param name 装配体名称（不含 .glb / .sldasm 等扩展名）
   */
  public setCurrentAssemblyName(name: string): void {
    this.assemblyName = name;
    this.editingComponentName = null;
  }

  /**
   * 获取当前正在编辑的装配体名称。
   * web-client 应优先通过此方法获取原始名称，而非自行维护 projectName。
   */
  public getCurrentAssemblyName(): string {
    return this.assemblyName ?? '';
  }

  /**
   * 进入编辑零部件模式。
   * 调用后 getDocumentTitle 将返回 “零件名 ← 装配体名” 格式。
   *
   * @param componentName 被编辑的零件或子装配体名称
   * @throws Error 当尚未调用 setCurrentAssemblyName 时抛出
   */
  public enterEditComponent(componentName: string): void {
    if (!this.assemblyName) {
      throw new Error(
        'EditingFocusManager: 必须先调用 setCurrentAssemblyName 设置装配体名称后再进入编辑模式'
      );
    }
    this.editingComponentName = componentName;
  }

  /**
   * 退出编辑零部件模式。
   * 调用后 getDocumentTitle 将恢复为仅显示装配体名称。
   */
  public exitEditComponent(): void {
    this.editingComponentName = null;
  }

  /**
   * 获取当前文档标题文本。
   * 规则：
   * - 有编辑焦点 → `${editingComponentName} ← ${assemblyName}`
   * - 无编辑焦点 → `${assemblyName}`
   * - 未设置任何名称 → 空字符串
   */
  public getDocumentTitle(): string {
    if (this.editingComponentName && this.assemblyName) {
      return `${this.editingComponentName} ← ${this.assemblyName}`;
    }
    return this.assemblyName ?? '';
  }

  /**
   * 释放管理器持有的所有状态。
   * 符合资源生命周期要求：任何创建的资源必须有释放路径。
   */
  public dispose(): void {
    this.assemblyName = null;
    this.editingComponentName = null;
  }
}
